"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.v1PromotionId = v1PromotionId;
exports.v1PromotionIndex = v1PromotionIndex;
exports.effectiveTaxIdForMode = effectiveTaxIdForMode;
exports.menuComponentsOfV1 = menuComponentsOfV1;
exports.fromV1Order = fromV1Order;
const resolve_1 = require("../cart/resolve");
const errors_1 = require("../errors");
const PROMOTION_ID_SEPARATOR = ':';
// Identifiant d'une promotion convertie : rang dans `order.promotions` +
// identifiant v1. Le rang lève l'ambiguïté des occurrences répétées d'une même
// promotion (point 3 ci-dessus).
function v1PromotionId(index, promotion) {
    return `${index}${PROMOTION_ID_SEPARATOR}${promotion._id ?? 'unknown'}`;
}
function v1PromotionIndex(promotionId) {
    const separator = promotionId.indexOf(PROMOTION_ID_SEPARATOR);
    return separator === -1 ? -1 : Number.parseInt(promotionId.slice(0, separator), 10);
}
function referenceOf(item, index) {
    return item.referenceId ?? item._id ?? `item-${index}`;
}
function taxIndexOf(taxes) {
    const index = new Map();
    for (const tax of taxes) {
        index.set(tax._id, { id: tax._id, ratePercent: tax.rate });
    }
    return index;
}
function resolveTaxRef(taxes, taxId, label) {
    // F4 : une taxe absente de `order.taxes` ne peut pas être devinée — la v1
    // écrivait un `console.error` puis comptait le montant sous la clé
    // `undefined` (order.interface.ts:344). Ici c'est un échec.
    if (taxId === null || taxId === undefined) {
        throw new errors_1.UnknownReferenceError(`fromV1Order : ${label} sans identifiant de taxe`);
    }
    const tax = taxes.get(taxId);
    if (tax === undefined) {
        throw new errors_1.UnknownReferenceError(`fromV1Order : taxe inconnue « ${taxId} » (${label})`);
    }
    return tax;
}
function optional(value) {
    return value === null || value === undefined ? undefined : value;
}
function pricingOf(priced) {
    const pricing = { base: priced.price ?? 0 };
    const delivery = optional(priced.deliveryPrice);
    const takeAway = optional(priced.takeAwayPrice);
    const onSpot = optional(priced.onSpotPrice);
    if (delivery !== undefined)
        pricing.delivery = delivery;
    if (takeAway !== undefined)
        pricing.takeAway = takeAway;
    if (onSpot !== undefined)
        pricing.onSpot = onSpot;
    return pricing;
}
// Taxe EFFECTIVE d'une entité pour le mode de la commande. R2 : le mode « sur
// place » résout `onSpotTaxId` — la v1 résolvait `takeAwayTaxId` ici, c'est
// l'écart `ONSPOT_TAX` du différentiel.
function effectiveTaxIdForMode(priced, mode) {
    switch (mode) {
        case 'DELIVERY':
            return priced.deliveryTaxId ?? priced.taxId;
        case 'TAKEAWAY':
            return priced.takeAwayTaxId ?? priced.taxId;
        case 'ONSPOT':
            return priced.onSpotTaxId ?? priced.taxId;
    }
}
function optionalTaxRef(taxes, taxId) {
    return taxId === null || taxId === undefined ? undefined : taxes.get(taxId);
}
// Résolution PARESSEUSE (ruling revue 1) : seule la taxe effectivement
// appliquée — celle du mode de la commande, repli sur `taxId` — doit exister
// dans `order.taxes` ; son absence est un échec (F4). Les identifiants des
// autres modes sont recopiés quand ils sont résolvables et simplement omis
// sinon : un `deliveryTaxId` périmé sur une commande à emporter ne concerne
// aucun montant et ne doit pas faire échouer la conversion.
function taxesOf(priced, taxes, mode, label) {
    const effective = resolveTaxRef(taxes, effectiveTaxIdForMode(priced, mode), label);
    // `base` sert de repli à `resolveTax` : quand `taxId` lui-même n'est pas
    // résolvable, c'est la taxe effective qui tient ce rôle, de sorte que
    // `resolveTax(result, mode)` rende toujours exactement `effective`.
    const result = { base: optionalTaxRef(taxes, priced.taxId) ?? effective };
    const delivery = optionalTaxRef(taxes, priced.deliveryTaxId);
    const takeAway = optionalTaxRef(taxes, priced.takeAwayTaxId);
    const onSpot = optionalTaxRef(taxes, priced.onSpotTaxId);
    if (delivery !== undefined)
        result.delivery = delivery;
    if (takeAway !== undefined)
        result.takeAway = takeAway;
    if (onSpot !== undefined)
        result.onSpot = onSpot;
    return result;
}
// v1 `getOptionPrice` : une ligne par produit d'option de quantité non nulle,
// la quantité s'entendant PAR unité de l'article parent.
function optionLines(options, taxes, mode, prefix) {
    const lines = [];
    for (const option of options ?? []) {
        for (const product of option.products ?? []) {
            if (product.quantity <= 0)
                continue;
            const variation = product.productVariation;
            if (!variation) {
                throw new errors_1.UnknownReferenceError(`fromV1Order : produit d'option sans variation (${prefix})`);
            }
            lines.push({
                referenceId: `${prefix}#option${lines.length}`,
                pricing: pricingOf(variation),
                taxes: taxesOf(variation, taxes, mode, `option ${variation._id}`),
                quantity: product.quantity,
            });
        }
    }
    return lines;
}
function stepSupplements(steps, taxes, mode, prefix) {
    const supplements = [];
    const options = [];
    for (const packToStep of steps ?? []) {
        for (const choice of packToStep.step.stepToProductVariations ?? []) {
            const quantity = choice.quantity ?? 0;
            const price = choice.price ?? 0;
            if (quantity > 0 && price > 0) {
                const variation = choice.productVariation;
                if (!variation) {
                    throw new errors_1.UnknownReferenceError(`fromV1Order : supplément d'étape sans variation (${prefix})`);
                }
                supplements.push({
                    // R4 : le supplément est taxé au taux de SON produit, pas à celui du menu.
                    referenceId: `${prefix}#step${supplements.length}`,
                    price,
                    quantity,
                    // v1 `getOrderTaxes` taxe le supplément d'étape au taux de SA
                    // variation, résolu par mode (order.interface.ts:391-397).
                    tax: resolveTaxRef(taxes, effectiveTaxIdForMode(variation, mode), `supplément d'étape ${variation._id}`),
                });
            }
            if (quantity > 0) {
                options.push(...optionLines(choice.options, taxes, mode, `${prefix}#stepOption${options.length}`));
            }
        }
    }
    return { supplements, options };
}
// Composants inclus dans le prix d'un menu (spec 2026-10-01 §3) : produits
// fixes (quantité 1) et choix d'étape retenus, avec ou sans supplément. Poids
// UNITAIRE = prix de vente seul dans le mode × quantité ; taxe du mode,
// résolue comme celle d'un supplément. Une entrée sans variation (stubs de
// l'eshop, prevalidate) ou sans identifiant de taxe pour le mode (le
// catalogue autorise une variation non taxée) ne pèse rien : elle ne doit
// jamais bloquer la caisse. Un identifiant PRÉSENT mais absent de
// `order.taxes` reste une erreur (F4).
function menuComponentsOf(pack, taxes, mode) {
    const components = [];
    const push = (variation, quantity) => {
        if (!variation || quantity <= 0)
            return;
        const taxId = effectiveTaxIdForMode(variation, mode);
        if (taxId === null || taxId === undefined)
            return;
        components.push({
            tax: resolveTaxRef(taxes, taxId, `composant de menu ${variation._id}`),
            weight: (0, resolve_1.resolvePrice)(pricingOf(variation), mode) * quantity,
        });
    };
    for (const fixed of pack.packToProductVariations ?? [])
        push(fixed.productVariation, 1);
    for (const packToStep of pack.packToSteps ?? []) {
        for (const choice of packToStep.step.stepToProductVariations ?? []) {
            push(choice.productVariation, choice.quantity ?? 0);
        }
    }
    return components;
}
// Même résolution pour le serveur, qui ventile les commandes non signées
// (OrderHelpers.getOrderTaxes) : une seule lecture des composants d'un menu.
function menuComponentsOfV1(pack, taxes, mode) {
    return menuComponentsOf(pack, taxIndexOf(taxes), mode);
}
function cartItemOf(item, index, taxes, mode) {
    const referenceId = referenceOf(item, index);
    // M2 : la v1 facture UNE fois une ligne dont la quantité est absente ou nulle
    // (`item.item.quantity || 1`, item.helpers.ts `getPriceForItem`). Le repli est
    // repris tel quel : sans lui, une commande que la v1 encaissait sans broncher
    // bloquerait la caisse (`InvalidQuantityError`) au lieu de l'encaisser.
    // Une quantité non entière, elle, reste un refus (computeItem).
    const quantity = item.item.quantity || 1;
    if (item.type === 'pack') {
        const pack = item.item;
        const stepped = stepSupplements(pack.packToSteps, taxes, mode, referenceId);
        const packOptions = (pack.packToProductVariations ?? []).flatMap((packToProductVariation, position) => optionLines(packToProductVariation.options, taxes, mode, `${referenceId}#pack${position}`));
        return {
            kind: 'pack',
            referenceId,
            quantity,
            pricing: pricingOf(pack),
            taxes: taxesOf(pack, taxes, mode, `menu ${pack._id}`),
            stepSupplements: stepped.supplements,
            options: [...packOptions, ...stepped.options],
            components: menuComponentsOf(pack, taxes, mode),
        };
    }
    const product = item.item;
    return {
        kind: 'product',
        referenceId,
        quantity,
        pricing: pricingOf(product.productVariation),
        taxes: taxesOf(product.productVariation, taxes, mode, `variation ${product.productVariation._id}`),
        options: optionLines(product.options, taxes, mode, referenceId),
    };
}
function variationIdOf(item) {
    return item.type === 'product' ? item.item.productVariation._id : undefined;
}
function packIdOf(item) {
    return item.type === 'pack' ? item.item._id : undefined;
}
// Résolution catalogue → lignes : les identifiants de variations (ou de menus)
// portés par une promotion deviennent les références des lignes du panier qui
// les portent (ruling Task 4 : plus simple qu'un second identifiant sur la
// ligne, et suffisant puisque le moteur ne raisonne que par ligne).
function linesMatching(items, ids, kind) {
    const wanted = new Set(ids);
    const references = [];
    items.forEach((item, index) => {
        const id = kind === 'PACKS' ? packIdOf(item) : variationIdOf(item);
        if (id !== undefined && wanted.has(id)) {
            references.push(referenceOf(item, index));
        }
    });
    return references;
}
function stringList(value) {
    return Array.isArray(value) ? value.filter((entry) => typeof entry === 'string') : [];
}
function numberOf(value) {
    return typeof value === 'number' ? value : 0;
}
function strategyOf(strategy) {
    switch (strategy) {
        case 'CHEAPEST':
            return 'CHEAPEST';
        case 'BEST_VALUE':
            return 'BEST_VALUE';
        case 'MOST_EXPENSIVE':
            return 'MOST_EXPENSIVE';
        default:
            return 'ALL';
    }
}
function actionOf(action) {
    const details = action.details ?? {};
    switch (action.type) {
        case 'order_discount_fixed':
            return { type: 'ORDER_DISCOUNT_FIXED', value: numberOf(details.value) };
        case 'order_discount_percentage':
            return { type: 'ORDER_DISCOUNT_PERCENTAGE', value: numberOf(details.value) };
        case 'items_discount_fixed':
            return {
                type: 'ITEMS_DISCOUNT_FIXED',
                value: numberOf(details.value),
                targets: stringList(details.targets),
                strategy: strategyOf(details.strategy),
            };
        case 'items_discount_percentage':
            return {
                type: 'ITEMS_DISCOUNT_PERCENTAGE',
                value: numberOf(details.value),
                targets: stringList(details.targets),
                strategy: strategyOf(details.strategy),
            };
        default:
            // `add_product_variation` / `add_pack` : la v1 n'en tire pas de remise,
            // seulement un groupe de candidats à vérifier (collectActionsCandidates,
            // promotion.ts:804-836) — il est émis comme condition, pas comme action.
            return undefined;
    }
}
function conditionsOf(promotion, items) {
    const conditions = [];
    for (const condition of promotion.conditions ?? []) {
        const details = condition.details ?? {};
        const referenceId = condition.referenceId;
        if (referenceId === null || referenceId === undefined)
            continue;
        if (condition.type === 'product_variations') {
            conditions.push({
                referenceId,
                kind: 'PRODUCT_VARIATIONS',
                ids: linesMatching(items, stringList(details.value), 'PRODUCT_VARIATIONS'),
                quantity: details.quantity ?? 1,
            });
        }
        if (condition.type === 'packs') {
            conditions.push({
                referenceId,
                kind: 'PACKS',
                ids: linesMatching(items, stringList(details.value), 'PACKS'),
                quantity: details.quantity ?? 1,
            });
        }
    }
    for (const action of promotion.actions ?? []) {
        const referenceId = action.referenceId;
        if (referenceId === null || referenceId === undefined)
            continue;
        const value = action.details?.value;
        if (action.type === 'add_product_variation' && typeof value === 'string') {
            conditions.push({
                referenceId,
                kind: 'PRODUCT_VARIATIONS',
                ids: linesMatching(items, [value], 'PRODUCT_VARIATIONS'),
                quantity: 1,
            });
        }
        if (action.type === 'add_pack' && typeof value === 'string') {
            conditions.push({
                referenceId,
                kind: 'PACKS',
                ids: linesMatching(items, [value], 'PACKS'),
                quantity: 1,
            });
        }
    }
    return conditions;
}
function orderTotalConditionOf(promotion) {
    for (const condition of promotion.conditions ?? []) {
        if (condition.type !== 'order_total')
            continue;
        const details = condition.details ?? {};
        return {
            operator: details.operator === '$lte' ? 'lte' : 'gte',
            value: numberOf(details.value),
        };
    }
    return undefined;
}
function promotionOf(promotion, index, items) {
    const id = v1PromotionId(index, promotion);
    // Ordre du discriminant v1 (getAllPromotionsTypesComputed) : actions, puis
    // code, puis LEAST_EXPENSIVE.
    const actions = (promotion.actions ?? []).flatMap((action) => {
        const converted = actionOf(action);
        return converted === undefined ? [] : [converted];
    });
    if ((promotion.actions ?? []).length > 0) {
        const orderTotalCondition = orderTotalConditionOf(promotion);
        const converted = {
            id,
            family: 'ACTIONS',
            priority: promotion.priority ?? 0,
            conditions: conditionsOf(promotion, items),
            actions,
        };
        if (orderTotalCondition !== undefined) {
            converted.orderTotalCondition = orderTotalCondition;
        }
        return converted;
    }
    if (promotion.code !== null && promotion.code !== undefined) {
        return {
            id,
            family: 'CODE',
            code: promotion.code,
            discountMode: promotion.discountMode ?? 'FIXED',
            discountValue: promotion.discountValue ?? 0,
        };
    }
    if (promotion.type === 'LEAST_EXPENSIVE') {
        return {
            id,
            family: 'LEAST_EXPENSIVE',
            discountMode: promotion.discountMode ?? 'FIXED',
            // `discountValue` nul = article entièrement offert (v1 getComputedDiscount).
            discountValue: promotion.discountValue ?? 0,
            neededCount: promotion.neededCount ?? 1,
            offeredCount: promotion.offeredCount ?? 1,
            // Chaque occurrence stockée vaut une application (point 3 de l'en-tête).
            cumulative: false,
            discountableReferenceIds: linesMatching(items, promotion.discountableProductVariationsIds ?? [], 'PRODUCT_VARIATIONS'),
        };
    }
    // `DISCOUNT` (ou `GIVEAWAY`) sans code ni action : la v1 ne la range dans
    // aucun de ses trois seaux et ne calcule donc rien.
    return undefined;
}
function isTargeted(discount) {
    return (discount.itemReferenceIds ?? []).length > 0 || (discount.itemIndices ?? []).length > 0;
}
function discountOf(discount) {
    const targets = {};
    if ((discount.itemReferenceIds ?? []).length > 0) {
        targets.itemReferenceIds = [...(discount.itemReferenceIds ?? [])];
    }
    else if ((discount.itemIndices ?? []).length > 0) {
        targets.itemIndices = [...(discount.itemIndices ?? [])];
    }
    return discount.type === 'FIXED'
        ? { type: 'FIXED', amount: discount.amount, ...targets }
        : { type: 'PERCENTAGE', percent: discount.amount, ...targets };
}
function fromV1Order(order, options) {
    const taxes = taxIndexOf(order.taxes ?? []);
    const v1Items = order.items ?? [];
    const items = v1Items.map((item, index) => cartItemOf(item, index, taxes, order.mode));
    const promotions = (order.promotions ?? []).flatMap((promotion, index) => {
        const converted = promotionOf(promotion, index, v1Items);
        return converted === undefined ? [] : [converted];
    });
    const v1Discounts = order.discounts ?? [];
    const discounts = [
        ...v1Discounts.filter(isTargeted).map(discountOf),
        ...v1Discounts.filter((discount) => !isTargeted(discount)).map(discountOf),
    ];
    const loyaltyDiscount = order.loyalty?.loyaltyDiscount ?? 0;
    if (loyaltyDiscount > 0) {
        discounts.push({ type: 'FIXED', amount: loyaltyDiscount });
    }
    const input = { items, mode: order.mode };
    if (promotions.length > 0)
        input.promotions = promotions;
    if (discounts.length > 0)
        input.discounts = discounts;
    if (order.mode === 'DELIVERY') {
        const fees = order.deliveryZone?.fees;
        const zone = fees
            ? {
                ...(optional(fees.amount) === undefined ? {} : { amount: fees.amount ?? 0 }),
                ...(optional(fees.reducedAmount) === undefined ? {} : { reducedAmount: fees.reducedAmount ?? 0 }),
                ...(optional(fees.reducedThreshold) === undefined
                    ? {}
                    : { reducedThreshold: fees.reducedThreshold ?? 0 }),
            }
            : undefined;
        input.delivery = zone === undefined
            ? { tax: options.deliveryTax(order) }
            : { zone, tax: options.deliveryTax(order) };
    }
    return input;
}
